import React, { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Plus, Users, CreditCard, TrendingUp, AlertTriangle, Package } from "lucide-react";
import { ModuleCatalogTab } from "./ModuleCatalogTab";
import { ClientLicensesTab } from "./ClientLicensesTab";
import { BillingHistoryTab } from "./BillingHistoryTab";
import { UsageMetricsTab } from "./UsageMetricsTab";
import { NewModuleDialog } from "./NewModuleDialog";
import { NewSubscriptionDialog } from "./NewSubscriptionDialog";
import { useClientLicenses } from "@/hooks/useClientLicenses";
import { useModulePrices } from "@/hooks/useModulePrices";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export const LicenseManagement = () => {
  const [isNewModuleDialogOpen, setIsNewModuleDialogOpen] = useState(false);
  const [isNewSubscriptionDialogOpen, setIsNewSubscriptionDialogOpen] = useState(false);
  
  const { clientLicenses, isLoading: licensesLoading } = useClientLicenses();
  const { modulePrices, isLoading: modulesLoading } = useModulePrices();

  // Fetch all client_modules for revenue calculation
  const { data: allClientModules = [] } = useQuery({
    queryKey: ['all-client-modules-admin'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('client_modules')
        .select('*')
        .eq('is_active', true);
      if (error) throw error;
      return data;
    }
  });

  // Calcular métricas modulares
  const activeLicenses = clientLicenses.filter(l => l.status === 'active').length;
  const suspendedLicenses = clientLicenses.filter(l => l.status === 'suspended').length;
  const trialLicenses = clientLicenses.filter(l => l.status === 'trial').length;
  
  // Receita = para cada cliente ativo, soma dos preços dos módulos ativos × contracted_seats
  const totalRevenue = clientLicenses
    .filter(l => l.status === 'active')
    .reduce((sum, license) => {
      const clientModules = allClientModules.filter(cm => cm.client_id === license.client_id);
      const moduleCost = clientModules.reduce((mSum, cm) => {
        const price = modulePrices.find(p => p.code === cm.module_code);
        return mSum + (price?.price || 0);
      }, 0);
      const seats = license.contracted_seats || 1;
      const cost = license.billing_cycle === 'yearly' ? moduleCost * 10 / 12 : moduleCost;
      return sum + (cost * seats);
    }, 0);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Gerenciamento de Assinaturas</h1>
          <p className="text-muted-foreground">
            Controle os módulos e assinaturas dos clientes
          </p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => setIsNewModuleDialogOpen(true)} className="gap-2">
            <Plus className="h-4 w-4" />
            Novo Módulo
          </Button>
          <Button variant="outline" onClick={() => setIsNewSubscriptionDialogOpen(true)} className="gap-2">
            <Users className="h-4 w-4" />
            Nova Assinatura
          </Button>
        </div>
      </div>

      {/* Métricas Overview */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Clientes Ativos</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{activeLicenses}</div>
            <p className="text-xs text-muted-foreground">Com assinaturas ativas</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Receita Mensal</CardTitle>
            <CreditCard className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              R$ {totalRevenue.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
            </div>
            <p className="text-xs text-muted-foreground">Receita recorrente (módulos × licenças)</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Trials Ativos</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{trialLicenses}</div>
            <p className="text-xs text-muted-foreground">Clientes em período trial</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Suspensas</CardTitle>
            <AlertTriangle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-destructive">{suspendedLicenses}</div>
            <p className="text-xs text-muted-foreground">Assinaturas suspensas</p>
          </CardContent>
        </Card>
      </div>

      {/* Tabs de Conteúdo */}
      <Tabs defaultValue="licenses" className="space-y-4">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="licenses">Assinaturas de Clientes</TabsTrigger>
          <TabsTrigger value="modules">Catálogo de Módulos</TabsTrigger>
          <TabsTrigger value="billing">Histórico de Cobrança</TabsTrigger>
          <TabsTrigger value="metrics">Métricas de Uso</TabsTrigger>
        </TabsList>

        <TabsContent value="licenses">
          <ClientLicensesTab />
        </TabsContent>

        <TabsContent value="modules">
          <ModuleCatalogTab />
        </TabsContent>

        <TabsContent value="billing">
          <BillingHistoryTab />
        </TabsContent>

        <TabsContent value="metrics">
          <UsageMetricsTab />
        </TabsContent>
      </Tabs>

      {/* Dialogs */}
      <NewModuleDialog 
        open={isNewModuleDialogOpen}
        onOpenChange={setIsNewModuleDialogOpen}
      />
      
      <NewSubscriptionDialog 
        open={isNewSubscriptionDialogOpen}
        onOpenChange={setIsNewSubscriptionDialogOpen}
      />
    </div>
  );
};
